import { useCallback, useMemo, type FC } from "react";
import { useListServiceAccounts } from "@/queries/service-account-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { useMembers } from "@/queries/organization-queries";
import { Principal, type Subject } from "gitpod-next-api/gitpod/v1/identity_pb";
import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { ServiceAccountAvatar } from "@/components/settings/organization/service-accounts/ServiceAccountAvatar";
import { cn } from "@/components/podkit/lib/cn";
import type { PlainMessage } from "@bufbuild/protobuf";
import { Combobox } from "@/components/flexkit/combobox";
import { Link } from "react-router-dom";
import { serviceAccountsUrl } from "@/utils/service-accounts";

type ExecutorOption = {
    id: string;
    name: string;
    principal: Principal.SERVICE_ACCOUNT | Principal.USER;
    avatarUrl?: string;
};

type PrebuildExecutorSelectProps = {
    /** The currently selected executor */
    selectedExecutor: PlainMessage<Subject>;
    /** Called when the user selects a new executor */
    onExecutorChange: (executor: PlainMessage<Subject>) => void;
    disabled?: boolean;
    className?: string;
};

export const PrebuildExecutorSelect: FC<PrebuildExecutorSelectProps> = ({
    selectedExecutor,
    onExecutorChange,
    disabled = false,
    className,
}) => {
    const { data: serviceAccountsData, isPending: isLoadingServiceAccounts } = useListServiceAccounts();
    const { data: currentUser, isPending: isLoadingUser } = useAuthenticatedUser();
    const { data: membersData, isPending: isLoadingMembers } = useMembers();

    // Selectable options: current user + service accounts (users can only set themselves or a service account)
    const executorOptions = useMemo((): ExecutorOption[] => {
        const options: ExecutorOption[] = [];

        if (currentUser) {
            options.push({
                id: currentUser.id,
                name: currentUser.name,
                principal: Principal.USER,
                avatarUrl: currentUser.avatarUrl,
            });
        }

        if (serviceAccountsData?.serviceAccounts) {
            serviceAccountsData.serviceAccounts.forEach((sa) => {
                options.push({
                    id: sa.id,
                    name: sa.name,
                    principal: Principal.SERVICE_ACCOUNT,
                });
            });
        }

        return options;
    }, [currentUser, serviceAccountsData]);

    // Resolve the selected executor to display info
    const displayedExecutor = useMemo((): ExecutorOption | undefined => {
        // First, check if it's in the selectable options
        const fromOptions = executorOptions.find((option) => option.id === selectedExecutor.id);
        if (fromOptions) {
            return fromOptions;
        }

        // For users not in selectable options, look up in org members (for display only)
        if (selectedExecutor.principal === Principal.USER && membersData?.members) {
            const member = membersData.members.find((m) => m.userId === selectedExecutor.id);
            if (member) {
                return {
                    id: member.userId,
                    name: member.fullName,
                    principal: Principal.USER,
                    avatarUrl: member.avatarUrl,
                };
            }
        }

        // For service accounts not in the list, show a fallback
        if (selectedExecutor.principal === Principal.SERVICE_ACCOUNT) {
            return {
                id: selectedExecutor.id,
                name: "Service Account",
                principal: Principal.SERVICE_ACCOUNT,
            };
        }

        return undefined;
    }, [executorOptions, selectedExecutor, membersData]);

    const handleExecutorChange = useCallback(
        (executorId: string) => {
            const executor = executorOptions.find((option) => option.id === executorId);
            if (executor) {
                onExecutorChange({
                    id: executor.id,
                    principal: executor.principal,
                });
            }
        },
        [executorOptions, onExecutorChange],
    );

    const isLoading = isLoadingServiceAccounts || isLoadingUser || isLoadingMembers;
    const hasServiceAccounts = (serviceAccountsData?.serviceAccounts?.length ?? 0) > 0;

    return (
        <div className={cn(className)}>
            <Combobox
                value={selectedExecutor.id}
                onValueChange={handleExecutorChange}
                disabled={disabled}
                loading={isLoading}
                className="w-full"
            >
                <Combobox.Value>
                    {displayedExecutor ? (
                        <div className="flex items-center gap-2">
                            <ExecutorAvatar option={displayedExecutor} />
                            <Combobox.ValueLabel>{displayedExecutor.name}</Combobox.ValueLabel>
                        </div>
                    ) : (
                        "Select identity"
                    )}
                </Combobox.Value>
                {isLoading ? (
                    <Combobox.Loading>Loading identities...</Combobox.Loading>
                ) : (
                    <Combobox.Popover>
                        <Combobox.List
                            items={executorOptions}
                            searchKeys={["name"]}
                            noMatchesComponent={<Combobox.Empty>No matching identities.</Combobox.Empty>}
                        >
                            {(option) => <ExecutorOptionItem key={option.id} option={option} />}
                        </Combobox.List>
                        <div className="border-t border-border-light px-3 py-2">
                            <Link
                                to={serviceAccountsUrl()}
                                className="text-sm text-content-secondary hover:text-content-primary"
                            >
                                {hasServiceAccounts ? "Manage service accounts" : "Create a service account"}
                            </Link>
                        </div>
                    </Combobox.Popover>
                )}
            </Combobox>
        </div>
    );
};

const ExecutorAvatar: FC<{ option: ExecutorOption }> = ({ option: { id, principal, avatarUrl, name } }) => {
    if (principal === Principal.USER) {
        return (
            <Avatar size={24}>
                {avatarUrl && <AvatarImage src={avatarUrl} alt={`${name}'s avatar`} />}
                <AvatarFallback>
                    <AvatarInitials name={name} size={24} />
                </AvatarFallback>
            </Avatar>
        );
    }
    return <ServiceAccountAvatar id={id} size={24} />;
};

const ExecutorOptionItem: FC<{ option: ExecutorOption }> = ({ option }) => {
    const { name } = option;
    return (
        <Combobox.ListItem value={option.id}>
            <Combobox.ListItemLeadingIcon>
                <ExecutorAvatar option={option} />
            </Combobox.ListItemLeadingIcon>
            <Combobox.ListItemTitle className="text-base">{name}</Combobox.ListItemTitle>
        </Combobox.ListItem>
    );
};
